import { Alert, Button, Col, Form, Row, Space, Tag, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { EventParticipantRole, PlatformUser } from "../repository/data.default";
import {
  addCalendarEvent,
  deleteCalendarEvent,
  getPlatformUsers,
  updateCalendarEvent,
} from "../repository/data.repository";
import { retry, getProblemMessage } from "../utils/http";
import { nameOf } from "../utils/typescript";
import { QueryOperation } from "../code/search-query/search-query";
import { Icons } from "../assets/icons";
import Icon from "@ant-design/icons";
import DynamicForm from "../controls/DynamicForm";
import { CalendarEventForm } from "./CalendarEventDialog.default";
import { originLabel } from "./CalendarView.default";
import {
  addEventInvitations,
  buildCalendarEventRequests,
  coreEventFields,
  ParticipantItem,
  participantRole,
} from "./calendar/calendarEventForm";
import DepartmentsMultiSelect from "./calendar/DepartmentsMultiSelect";
import EventParticipantsPanel from "./calendar/EventParticipantsPanel";
import ParticipantTargetSelect from "./calendar/ParticipantTargetSelect";
import TargetingPreview from "../controls/TargetingPreview";
import InModal from "../controls/InModal";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { InputObject } from "../controls/default";

export interface CalendarEventDialogProps {
  readonly open?: boolean;
  readonly title?: string;
  readonly mode: "preview" | "add" | "update" | "delete";
  readonly initialValues?: CalendarEventForm | null;
  readonly target?: {
    readonly type: "user";
    readonly uid: string;
  };
  readonly onMode?: (mode: "update" | "delete") => void;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
  // Deep-link to the originating domain record (e.g. open the counselling request or training
  // behind this event). `originType` tells the caller which flow to open.
  readonly onViewOrigin?: (originUid: string, originType: string) => void;
}

// Origin types that offer a deep-link action on the preview banner, mapped to the action label.
// Other origin types show the banner without an action.
const ORIGIN_ACTIONS: Record<string, string> = {
  CounsellingRequest: "View Request",
  Training: "View Training",
};

function CalendarEventDialog(props: CalendarEventDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<CalendarEventForm>([], form);
  // The host picker keeps its own form so an attendee pick never resets it (the attendee picker is
  // the self-clearing ParticipantTargetSelect and holds no form state at all).
  const [hostForm] = Form.useForm();
  // Staged participants only matter while creating (no event yet); on an existing event, picks invite
  // immediately and the participants panel re-fetches via refreshKey.
  const [staged, setStaged] = useState<ParticipantItem[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<InputObject[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const permissions = useSelector(authorizeSelectors.permissions);

  const requests = buildCalendarEventRequests(form);
  const isPreview = props.mode === "preview";
  const eventUid = props.initialValues?.uid;
  // Guards the one-time target prefill against re-runs from the inline `initialValues={{}}` some
  // callers pass (its identity changes every render, which would otherwise re-wipe the staged list).
  const prefilledRef = useRef(false);
  // The host/attendee pickers are scoped to the event's company; if the company is switched, staged picks
  // from the previous company no longer belong, so drop them. Only clears on an actual change (not the
  // initial set / population), so it never wipes the target prefill.
  const prevCompanyRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const current = values?.company?.value;
    if (prevCompanyRef.current !== undefined && current !== prevCompanyRef.current) {
      setStaged([]);
      // Departments belong to the previous company — a company switch invalidates them.
      setSelectedDepartments([]);
    }
    prevCompanyRef.current = current;
  }, [values?.company?.value]);

  useEffect(() => {
    if (!props.open) {
      reset();
      prefilledRef.current = false;
      prevCompanyRef.current = undefined;
      return;
    }

    form.setFieldsValue(props.initialValues);
    setSelectedDepartments((props.initialValues?.departments as InputObject[] | undefined) ?? []);
    // "Schedule event for this user" (from a user card) opens in add mode with a target — pre-stage
    // that user as an attendee, once per open. Existing events load their participants in the panel.
    if (!eventUid && props.target?.uid && !prefilledRef.current) {
      prefilledRef.current = true;
      prefillTargetUser(props.target.uid);
    }
  }, [props.open, props.initialValues]);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then((value) => setValid(!!Object.values(value).length))
      .catch(() => setValid(false));
  }, [form, values, props.open]);

  // Events

  // Add a freshly-picked participant. On an existing event it invites immediately (and the panel
  // refetches); while creating it stages in memory until the event is saved.
  async function addPicked(picked?: ParticipantItem): Promise<void> {
    if (!picked?.value) return;

    if (eventUid) {
      setLoading(true);
      setErrorMessage("");
      try {
        await addEventInvitations(eventUid, [picked]);
        setRefreshKey((k) => k + 1);
      } catch (err) {
        setErrorMessage(getProblemMessage(err));
      } finally {
        setLoading(false);
      }
    } else {
      stageParticipant(picked);
    }
  }

  // Stage a picked participant (create flow). De-dupes by type + uid and upgrades an existing
  // Attendee → Host on re-pick; never downgrades.
  function stageParticipant(picked: ParticipantItem): void {
    const keyOf = (item: ParticipantItem) => `${item.dataItem?.__type}|${item.value}`;
    setStaged((prev) => {
      const existing = prev.find((item) => keyOf(item) === keyOf(picked));
      if (!existing) return [...prev, picked];
      if (
        participantRole(picked) === EventParticipantRole.Host &&
        participantRole(existing) !== EventParticipantRole.Host
      ) {
        return prev.map((item) => (keyOf(item) === keyOf(picked) ? picked : item));
      }
      return prev;
    });
  }

  function onPickHost(vals: { host?: ParticipantItem }): void {
    if (vals?.host?.value) {
      addPicked(vals.host);
      hostForm.resetFields();
    }
  }

  function onRemoveStaged(index: number): void {
    setStaged((prev) => prev.filter((_, i) => i !== index));
  }

  function onSubmit(): void {
    const fail = (err?: any) => {
      setErrorMessage(getProblemMessage(err));
      setLoading(false);
    };

    if (props.mode === "add") {
      const { title, description, startDateTime, endDateTime, company, type } = values;
      const hostUserUids = staged
        .filter((i) => participantRole(i) === EventParticipantRole.Host)
        .map((i) => i.value);
      const attendees = staged.filter((i) => participantRole(i) === EventParticipantRole.Attendee);
      setLoading(true);
      setErrorMessage("");
      addCalendarEvent({
        title,
        description,
        startDateTime: startDateTime.toDate(),
        endDateTime: endDateTime.toDate(),
        companyUid: company?.value,
        departmentUids: selectedDepartments.map((d) => d.value),
        typeUid: type.value!,
        // The creator is added as host server-side; these are the additional hosts picked here.
        hostUserUids: hostUserUids.length ? hostUserUids : undefined,
      })
        .then((dataItem) =>
          addEventInvitations(dataItem.uid, attendees)
            .then(() => {
              setLoading(false);
              props.onFinish?.();
            })
            .catch(fail),
        )
        .catch(fail);
    } else if (props.mode === "update") {
      const { title, description, startDateTime, endDateTime, company, type } = values;
      setLoading(true);
      setErrorMessage("");
      // Participants are managed immediately on existing events; Save only commits the event details.
      updateCalendarEvent({
        uid: props.initialValues?.uid!,
        rowVersion: props.initialValues?.rowVersion!,
        title,
        description,
        startDateTime: startDateTime.toDate(),
        endDateTime: endDateTime.toDate(),
        companyUid: company?.value,
        departmentUids: selectedDepartments.map((d) => d.value),
        typeUid: type.value!,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(fail);
    } else if (props.mode === "delete") {
      setLoading(true);
      setErrorMessage("");
      deleteCalendarEvent(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(fail);
    }
  }

  function onMode(mode: "update" | "delete"): void {
    props.onMode?.(mode);
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setLoading(false);
    setValid(false);
    setErrorMessage("");
    setStaged([]);
    setSelectedDepartments([]);
    setRefreshKey(0);
    form.resetFields();
    hostForm.resetFields();
  }

  // Look up a single user and stage them as an attendee (used by the "schedule event for user" action).
  function prefillTargetUser(uid: string): void {
    setLoading(true);
    retry(() =>
      getPlatformUsers({
        searchQuery: {
          conditions: [
            { field: nameOf<PlatformUser>("uid"), operation: QueryOperation.Equal, values: [uid] },
          ],
        },
        pageNumber: 0,
        pageSize: 1,
      }),
    )
      .then(({ data }) => {
        setLoading(false);
        setStaged(
          data.map((d) => ({
            display: [d.firstName, d.lastName].join(" "),
            subDisplay: d.companyName,
            value: d.uid,
            dataItem: {
              __name: [d.companyName, "Attendee"].filter(Boolean).join(" · "),
              __type: "user",
              __role: EventParticipantRole.Attendee,
            },
          })),
        );
        // S3: preselect the target user's company. Without this the admin had to pick a company before
        // other pickers worked, and picking any company other than the target's would wipe the staged
        // attendee via the "company changed → clear" effect — the main offender being: picking the
        // target's OWN company (because the picker's value identity changes from undefined to a new
        // InputObject). Preselecting it up front satisfies prevCompanyRef and keeps the attendee staged.
        const resolved = data[0];
        if (resolved?.companyUid) {
          form.setFieldValue("company", {
            display: resolved.companyName,
            value: resolved.companyUid,
          });
        }
      })
      .catch((err) => {
        setLoading(false);
        setErrorMessage(getProblemMessage(err));
      });
  }

  function createTitle(): string {
    switch (props.mode) {
      case "add":
        return "Add Event";
      case "update":
        return "Edit Event";
      case "delete":
        return "Remove Event";
      case "preview":
        return "Preview Event";
    }
  }

  function createMode(): string {
    switch (props.mode) {
      case "add":
        return "Add";
      case "update":
        return "Save";
      case "preview":
      case "delete":
        return "Delete";
    }
  }

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={
        <Row className="flex flex-row items-center overflow-y-auto">
          <Typography.Text className="flex-auto">{createTitle()}</Typography.Text>
          {isPreview &&
            (props.initialValues?.originType ? (
              // System-managed event (e.g. a counselling session): read-only here, managed from its source flow.
              <Tag color="purple" className="-mt-1 mr-10 self-end">
                {originLabel(props.initialValues.originType)}
              </Tag>
            ) : (
              <Space className="-mt-2 mr-10 self-end">
                <Button
                  type={"text"}
                  className={`${!permissions.can("CalendarEvents", "edit") ? "disabled" : ""}`}
                  icon={<Icon className="-ml-0.5 text-2xl" component={Icons.EditIcon} />}
                  onClick={() => onMode("update")}
                />
                <Button
                  type={"text"}
                  className={`${!permissions.can("CalendarEvents", "delete") ? "disabled" : ""}`}
                  icon={<Icon className="-ml-0.5 text-2xl" component={Icons.DeleteIcon} />}
                  onClick={() => onMode("delete")}
                />
              </Space>
            ))}
        </Row>
      }
      onCancel={onCancel}
      actions={
        !isPreview && (
          <Space>
            <Button disabled={loading} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type={"primary"}
              danger={props.mode === "delete"}
              loading={loading}
              disabled={!valid && props.mode !== "delete"}
              onClick={onSubmit}
            >
              {createMode()}
            </Button>
          </Space>
        )
      }
    >
      {props.mode !== "delete" && (
        <Row className="mb-2 flex flex-row flex-nowrap overflow-hidden overflow-y-auto">
          <Col className="flex flex-1 flex-col">
            <DynamicForm
              className="w-full"
              disabled={loading}
              form={form}
              preview={isPreview}
              schema={{ groups: [{ controls: coreEventFields(requests, isPreview) }] }}
            />
            <div className="mt-2 p-1">
              <DepartmentsMultiSelect
                label="Departments"
                companyUid={values?.company?.value}
                value={selectedDepartments}
                onChange={setSelectedDepartments}
                readOnly={isPreview}
                disabled={loading}
                loadDepartments={requests.scopedDepartmentsRequest}
              />
            </div>
          </Col>
          <Col className="flex flex-1 flex-col">
            {!isPreview && (
              <>
                <DynamicForm
                  className="w-full"
                  form={hostForm}
                  schema={{
                    groups: [
                      {
                        controls: [
                          {
                            type: "combobox",
                            label: "Add Host",
                            name: "host",
                            request: requests.hostsRequest,
                          },
                        ],
                      },
                    ],
                  }}
                  onChange={onPickHost}
                />
                <ParticipantTargetSelect
                  className="w-full"
                  label="Add Attendee"
                  companyUid={values?.company?.value}
                  disabled={loading}
                  onPick={addPicked}
                />
              </>
            )}
            <EventParticipantsPanel
              eventUid={eventUid}
              staged={staged}
              editable={!isPreview}
              refreshKey={refreshKey}
              onRemoveStaged={onRemoveStaged}
            />
            {/* Reach preview for the staged set (create flow only — `staged` is only populated while
                creating; on an existing event picks invite immediately and the panel shows reality).
                Hosts count as reached users too. */}
            {!!staged.length && (
              <TargetingPreview
                className="mt-1 pl-1"
                targets={staged.map((item) => ({
                  type: item.dataItem?.__type ?? "user",
                  uid: item.value,
                  label: item.display,
                }))}
              />
            )}
          </Col>
        </Row>
      )}
      {isPreview && props.initialValues?.originType && (
        <Alert
          className="mt-1"
          type="info"
          showIcon
          message={`This is a ${originLabel(props.initialValues.originType)} created from another flow. Manage its schedule from the originating page (e.g. Counselling Requests) — editing it here is disabled to keep both in sync.`}
          action={
            ORIGIN_ACTIONS[props.initialValues.originType] &&
            props.initialValues.originUid &&
            props.onViewOrigin ? (
              <Button
                size="small"
                type="link"
                onClick={() =>
                  props.onViewOrigin!(
                    props.initialValues!.originUid!,
                    props.initialValues!.originType!,
                  )
                }
              >
                {ORIGIN_ACTIONS[props.initialValues.originType]}
              </Button>
            ) : undefined
          }
        />
      )}
      {props.mode === "delete" && (
        <Typography.Paragraph>
          <Typography.Text>
            Continue deleting the{" "}
            <Typography.Text className="font-bold">{props.title}</Typography.Text> event?
          </Typography.Text>
        </Typography.Paragraph>
      )}
      {errorMessage && (
        <Alert className="mb-2" message={errorMessage} type="error" />
      )}
    </InModal>
  );
}

export default CalendarEventDialog;
